import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Icon } from 'src/components/atoms/Icon';
import { useAppContext } from 'src/contexts/AppContext';
import { useFilterContext } from 'src/contexts/FilterContext';
import {
  useGoalsStatsQuery,
  useGoalDetailQuery,
  useCreateCommentMutation,
  useUpdateCommentMutation,
  useDeleteCommentMutation,
} from 'src/api/goals';
import { useUsersQuery } from 'src/api/users';
import { GoalDetailsDrawer } from 'src/features/Goals/components/molecules/GoalDetailsDrawer';
import { GoalsListModal } from 'src/features/Goals/components/organisms/GoalsListModal';
import { Goal, GoalDetail, ModelType } from 'src/features/Goals/types';
import { MODEL_TYPE_PATHS } from 'src/features/Goals/utils/formatters';
import { AppServices } from 'src/app/routes/utils';

const APP_SERVICE_TO_MODEL_TYPE: Partial<Record<AppServices, ModelType>> = {
  [AppServices.DA]: 'da',
  [AppServices.MM]: 'mm',
  [AppServices.MO]: 'mo',
  [AppServices.LT]: 'lt',
};

export const ActiveGoalsWidget: React.FC = () => {
  const navigate = useNavigate();
  const { currentCustomer } = useAppContext();
  const { currentAppService } = useFilterContext();
  const customerId = currentCustomer?.customerName ?? '';

  const modelType = currentAppService
    ? APP_SERVICE_TO_MODEL_TYPE[currentAppService]
    : undefined;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null);

  const { data: stats } = useGoalsStatsQuery(
    customerId,
    modelType ?? ('da' as ModelType),
  );
  const { data: goalDetail } = useGoalDetailQuery(customerId, selectedGoalId);
  const { data: users = [] } = useUsersQuery(customerId);

  const createCommentMutation = useCreateCommentMutation(
    customerId,
    selectedGoalId ?? '',
  );
  const updateCommentMutation = useUpdateCommentMutation(
    customerId,
    selectedGoalId ?? '',
  );
  const deleteCommentMutation = useDeleteCommentMutation(
    customerId,
    selectedGoalId ?? '',
  );

  const usersForDrawer = useMemo(
    () =>
      users.map((u) => ({
        id: u.id,
        name: u.displayName ?? u.email,
        avatarUrl: undefined,
      })),
    [users],
  );

  const handleWidgetClick = useCallback(() => {
    if (!stats) return;

    if (stats.count === 1 && stats.goals.length > 0) {
      setSelectedGoalId(stats.goals[0].id);
      setIsDrawerOpen(true);
    } else if (stats.count >= 2) {
      setIsModalOpen(true);
    }
  }, [stats]);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedGoalId(null);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleViewDetails = useCallback((goal: Goal) => {
    setSelectedGoalId(goal.id);
    setIsDrawerOpen(true);
    setIsModalOpen(false);
  }, []);

  const handleViewLinkedData = useCallback(
    (goal: Goal | GoalDetail) => {
      const path = MODEL_TYPE_PATHS[goal.modelType];
      navigate(path, {
        state: {
          filteredByGoal: goal.id,
          modelParametersId: goal.modelParametersId,
          timeRange: {
            start: goal.goalPeriodStart,
            end: goal.goalPeriodEnd,
          },
          campaigns:
            'campaigns' in goal && goal.campaigns
              ? goal.campaigns.map((c) => c.id)
              : undefined,
          mediaChannels: goal.mediaChannels,
        },
      });
      setIsDrawerOpen(false);
      setIsModalOpen(false);
    },
    [navigate],
  );

  const handleSeeAllGoals = useCallback(() => {
    navigate('/goals-and-targets');
    setIsModalOpen(false);
  }, [navigate]);

  const handleAddComment = useCallback(
    async (content: string, parentId?: string, mentionedUserIds?: string[]) => {
      await createCommentMutation.mutateAsync({
        content,
        parentId,
        mentionedUserIds,
      });
    },
    [createCommentMutation],
  );

  const handleEditComment = useCallback(
    async (commentId: string, content: string, mentionedUserIds?: string[]) => {
      await updateCommentMutation.mutateAsync({
        commentId,
        data: { content, mentionedUserIds },
      });
    },
    [updateCommentMutation],
  );

  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      await deleteCommentMutation.mutateAsync(commentId);
    },
    [deleteCommentMutation],
  );

  // Hide widget if no goals or no model type
  if (!stats || stats.count === 0 || !modelType) {
    return null;
  }

  const singleGoal = stats.count === 1 && stats.goals.length > 0;

  return (
    <>
      <Box
        bg="surface.card"
        borderRadius="12px"
        px={{ base: 4, lg: 6 }}
        py={3}
        minW={{ base: 'auto', lg: '350px' }}
        w={{ base: '100%', lg: 'auto' }}
        border="1px solid"
        borderColor="border.layout"
        boxShadow="sm"
        cursor="pointer"
        onClick={handleWidgetClick}>
        <Flex justifyContent="space-between" alignItems="center">
          <Box>
            <Text
              fontSize="sm"
              color="gray.400"
              fontFamily="marketingPage"
              mb={1}>
              Active Goals
            </Text>
            {singleGoal ? (
              <>
                <Text
                  fontSize="lg"
                  fontWeight="500"
                  color="text.title"
                  fontFamily="marketingPage"
                  noOfLines={1}>
                  {stats.goals[0].title}
                </Text>
                <Text
                  fontSize="sm"
                  color="icon.green"
                  fontFamily="marketingPage">
                  {Math.round(stats.goals[0].progressPercent)}% progress
                </Text>
              </>
            ) : (
              <>
                <Flex alignItems="center" gap={2}>
                  <Text
                    fontSize="xl"
                    fontWeight="500"
                    color="text.title"
                    fontFamily="marketingPage">
                    {stats.count} Goals
                  </Text>
                </Flex>
                <Flex alignItems="center" gap={1}>
                  <Text
                    fontSize="sm"
                    color="icon.green"
                    fontFamily="marketingPage">
                    {stats.completionRate.toFixed(1)}%
                  </Text>
                  <Text
                    color="gray.400"
                    fontSize="sm"
                    fontFamily="marketingPage">
                    of total goals completed
                  </Text>
                </Flex>
              </>
            )}
          </Box>
          <Box
            w="50px"
            h="50px"
            bg="surface.control"
            rounded="8px"
            display="flex"
            alignItems="center"
            justifyContent="center">
            <Icon iconName="arrow-graph" w={6} h={6} color="text.title" />
          </Box>
        </Flex>
      </Box>

      <GoalsListModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        goals={stats.goals}
        onViewDetails={handleViewDetails}
        onViewLinkedData={handleViewLinkedData}
        onSeeAllGoals={handleSeeAllGoals}
      />

      <GoalDetailsDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        goal={goalDetail ?? null}
        onViewLinkedData={handleViewLinkedData}
        onAddComment={handleAddComment}
        onEditComment={handleEditComment}
        onDeleteComment={handleDeleteComment}
        isLoadingComments={createCommentMutation.isLoading}
        users={usersForDrawer}
      />
    </>
  );
};
